import { useEffect, useMemo, useState } from 'react'
import { useFormat, useI18n, type MessageKey } from '@/i18n'
import { retryCommand, type OpenedOutboxRecord } from '@/offline'
import type { MediaRecord } from '@/offline/db'
import { retryLostMedia, uploadMedia } from '@/offline/media'
import { httpTransport } from '@/offline/transport'
import { redact } from '@/offline/exportData'
import { Btn, Cta, V1Sheet, WaitCta } from '@/features/gate/v1/kit'
import { C } from '@/features/gate/v1/style'
import { photoIdsOf, type RecordSummary } from './summary'
import { useRecordPhotos, type PhotoState } from './photos'

function Thumb({ rec, state }: { rec: MediaRecord | undefined; state: PhotoState }) {
  const { t } = useI18n()
  const blob = state === 'LOST' ? null : (rec?.blob ?? null)
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob])
  useEffect(() => () => void (url && URL.revokeObjectURL(url)), [url])
  const tone =
    state === 'SENT'
      ? { fg: C.ok, bg: C.okWash }
      : state === 'WAITING'
        ? { fg: C.accDark, bg: C.warnWash }
        : { fg: C.bad, bg: C.badWash }
  return (
    <figure style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div
        style={{
          aspectRatio: '3 / 4',
          borderRadius: 10,
          overflow: 'hidden',
          background: C.sunk,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: C.muted,
          fontSize: 13,
          textAlign: 'center',
          padding: url ? 0 : 6,
        }}
      >
        {url ? (
          <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          t(state === 'SENT' ? 'sync.p.onServer' : 'sync.p.noCopy')
        )}
      </div>
      <figcaption
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: tone.fg,
          background: tone.bg,
          borderRadius: 8,
          padding: '1px 6px',
          textAlign: 'center',
        }}
      >
        {t(`sync.p.${state}` as MessageKey)}
      </figcaption>
    </figure>
  )
}

/** Plain text of a record for the team (WhatsApp, SMS…): what it is, its state, the figures. */
function shareText(
  title: string,
  status: string,
  when: string,
  summary: RecordSummary,
  photos: Array<{ state: PhotoState; rec: MediaRecord | undefined }>,
  error: string | null,
  id: string,
): string {
  const sent = photos.filter((p) => p.state === 'SENT').length
  const lost = photos.filter((p) => p.state === 'LOST').length
  return [
    `${title} — ${status}`,
    when,
    ...summary.rows.map(([k, v]) => `${k}: ${v}`),
    photos.length ? `Photos: ${sent}/${photos.length} sent${lost ? `, ${lost} lost` : ''}` : null,
    error ? `Problem: ${error}` : null,
    `ID: ${id}`,
  ]
    .filter(Boolean)
    .join('\n')
}

export function RecordDetail({
  rec,
  title,
  statusWord,
  reason,
  summary,
  onSetAside,
  onClose,
}: {
  rec: OpenedOutboxRecord
  title: string
  statusWord: string
  reason: string | null
  summary: RecordSummary
  onSetAside: () => void
  onClose: () => void
}) {
  const { t } = useI18n()
  const fmt = useFormat()
  const ids = useMemo(() => photoIdsOf(rec, rec.payload), [rec])
  const photos = useRecordPhotos(ids)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [raw, setRaw] = useState(false)
  const unsent = photos.filter((p) => p.state === 'WAITING' || p.state === 'LOST')
  const lostIds = photos.filter((p) => p.state === 'LOST').map((p) => p.id)
  const photoErrors = [
    ...new Set(unsent.map((p) => p.rec?.last_error).filter((e): e is string => !!e)),
  ]

  const sendPhotos = async () => {
    setBusy(true)
    setNote(null)
    try {
      if (lostIds.length) await retryLostMedia(lostIds)
      const out = await uploadMedia(httpTransport.uploadMedia, {
        ids: unsent.map((p) => p.id),
        force: true,
      })
      setNote(t('sync.photos.result', { sent: out.uploaded.length, failed: out.failed.length }))
    } finally {
      setBusy(false)
    }
  }

  const share = async () => {
    const text = shareText(
      title,
      statusWord,
      fmt.dateTime(rec.created_at),
      summary,
      photos,
      reason ?? photoErrors[0] ?? null,
      rec.id,
    )
    try {
      if (navigator.share) await navigator.share({ title, text })
      else {
        await navigator.clipboard.writeText(text)
        setNote(t('sync.d.copied'))
      }
    } catch {
      /* the person closed the share sheet */
    }
  }

  return (
    <V1Sheet title={title} onClose={onClose} top={60}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 16 }}>
        <div>
          <b>{statusWord}</b>
          <span style={{ color: C.muted }}> · {fmt.dateTime(rec.created_at)}</span>
          {rec.acked_at && (
            <div style={{ color: C.muted, fontSize: 14 }}>
              {t('sync.d.reached', { when: fmt.dateTime(rec.acked_at) })}
            </div>
          )}
          {reason && <div style={{ color: C.bad, fontSize: 15, marginTop: 4 }}>{reason}</div>}
          {(rec.result?.exception_ids?.length ?? 0) > 0 && (
            <div style={{ color: C.accDark, fontSize: 15, marginTop: 4 }}>
              {t('sync.d.heldWhy', { count: rec.result!.exception_ids.length })}
            </div>
          )}
        </div>

        {summary.rows.length > 0 && (
          <dl
            style={{
              margin: 0,
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              columnGap: 12,
              rowGap: 4,
            }}
          >
            {summary.rows.map(([k, v], i) => (
              <div key={i} style={{ display: 'contents' }}>
                <dt style={{ color: C.muted }}>{k}</dt>
                <dd style={{ margin: 0, fontWeight: 600, wordBreak: 'break-word' }}>{v}</dd>
              </div>
            ))}
          </dl>
        )}

        {photos.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <b>
              {t('sync.d.photos', {
                sent: photos.filter((p) => p.state === 'SENT').length,
                total: photos.length,
              })}
            </b>
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 6 }}
            >
              {photos.map((p) => (
                <Thumb key={p.id} rec={p.rec} state={p.state} />
              ))}
            </div>
            {photoErrors.map((e) => (
              <div key={e} style={{ color: C.bad, fontSize: 14, wordBreak: 'break-word' }}>
                {e}
              </div>
            ))}
            {lostIds.length > 0 && (
              <div style={{ color: C.muted, fontSize: 14 }}>{t('sync.p.lostHelp')}</div>
            )}
            {unsent.length > 0 &&
              (busy ? (
                <WaitCta>{t('sync.photos.sending')}</WaitCta>
              ) : (
                <Btn icon="cloudup" onClick={() => void sendPhotos()}>
                  {t('sync.photos.sendNow')}
                </Btn>
              ))}
          </div>
        )}

        {note && <div style={{ fontWeight: 600 }}>{note}</div>}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {rec.status === 'ERROR' && (
            <Btn icon="undo" onClick={() => void retryCommand(rec.id)}>
              {t('common.retry')}
            </Btn>
          )}
          {rec.status === 'ERROR' && (
            <Btn icon="supervisor" onClick={onSetAside}>
              {t('sync.setAside')}
            </Btn>
          )}
          <Btn icon="book" onClick={() => setRaw((v) => !v)}>
            {t(raw ? 'sync.d.hideData' : 'sync.d.allData')}
          </Btn>
        </div>
        {raw && (
          <pre
            style={{
              margin: 0,
              padding: 10,
              borderRadius: 10,
              background: C.sunk,
              fontSize: 12,
              maxHeight: 280,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {JSON.stringify(redact(rec.payload), null, 2)}
          </pre>
        )}
        <Cta label={t('sync.d.share')} icon="cloudup" onClick={() => void share()} />
      </div>
    </V1Sheet>
  )
}
